/**
 * 知识库同步进度服务
 *
 * 使用 REST API 轮询获取知识库同步进度状态。
 * 已迁移至 HTTP 轮询，移除 WebSocket 依赖。
 */

// ==================== 类型定义 ====================

export interface SyncStartedData {
  sync_id: string;
  started_at: string;
}

export interface SyncProgressData {
  sync_id: string;
  stage: 'chroma' | 'neo4j';
  progress: number;
  current: string;
}

export interface SyncCompletedData {
  sync_id: string;
  chapters_synced: number;
  settings_synced: number;
  duration_ms: number;
}

export interface SyncFailedData {
  sync_id: string;
  error: string;
}

export interface KnowledgeSyncSubscribeOptions {
  onStarted?: (data: SyncStartedData) => void;
  onProgress?: (data: SyncProgressData) => void;
  onCompleted?: (data: SyncCompletedData) => void;
  onFailed?: (data: SyncFailedData) => void;
  onError?: (error: Error) => void;
  onClosed?: () => void;
}

interface ActiveSubscription {
  syncId: string;
  workspaceId: number;
  options: KnowledgeSyncSubscribeOptions;
  timer: ReturnType<typeof setInterval> | null;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const POLL_INTERVAL = 2000;

export class KnowledgeWebSocketService {
  private subscriptions: Map<string, ActiveSubscription> = new Map();

  subscribeSyncProgress(
    workspaceId: number,
    syncId: string,
    options: KnowledgeSyncSubscribeOptions = {}
  ): void {
    if (this.subscriptions.has(syncId)) {
      this.unsubscribeSyncProgress(syncId);
    }

    options.onStarted?.({
      sync_id: syncId,
      started_at: new Date().toISOString(),
    });

    const timer = setInterval(async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/knowledge/workspaces/${workspaceId}/sync-status?sync_id=${syncId}`,
          { credentials: 'include' }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data.status === 'completed') {
          options.onCompleted?.({
            sync_id: syncId,
            chapters_synced: data.chapters_synced || 0,
            settings_synced: data.settings_synced || 0,
            duration_ms: data.duration_ms || 0,
          });
          this.unsubscribeSyncProgress(syncId);
        } else if (data.status === 'failed') {
          options.onFailed?.({
            sync_id: syncId,
            error: data.error || '同步失败',
          });
          this.unsubscribeSyncProgress(syncId);
        } else if (data.progress !== undefined) {
          options.onProgress?.({
            sync_id: syncId,
            stage: data.stage || 'chroma',
            progress: data.progress,
            current: data.current || '',
          });
        }
      } catch (error) {
        console.error('[KnowledgeSyncService] 轮询同步状态失败:', error);
        options.onError?.(error instanceof Error ? error : new Error('轮询失败'));
      }
    }, POLL_INTERVAL);

    this.subscriptions.set(syncId, { syncId, workspaceId, options, timer });
  }

  unsubscribeSyncProgress(syncId: string): void {
    const sub = this.subscriptions.get(syncId);
    if (sub?.timer) {
      clearInterval(sub.timer);
    }
    this.subscriptions.delete(syncId);
  }

  close(): void {
    for (const [syncId] of this.subscriptions) {
      this.unsubscribeSyncProgress(syncId);
    }
  }

  dispose(): void {
    this.close();
  }
}

export const knowledgeWebSocketService = new KnowledgeWebSocketService();

export default knowledgeWebSocketService;
